import React, { type FC, useCallback } from 'react'

import { isTranslation } from '../../../../domain'

import { FormattedTranslation, useTranslator } from '../../Internationalization'
import { useColumn, useColumns } from './State'
import type { Columns, Line } from './Types'

export const ColumnLabel = <L extends Line>({ name }: Readonly<{ name: keyof Columns<L> }>): ReturnType<FC> => {
    const { label: LabelContent } = useColumn<L, keyof Columns<L>>(name)

    if (isTranslation(LabelContent)) {
        return <FormattedTranslation id={LabelContent} />
    }

    return typeof LabelContent === 'function' ? <LabelContent>{name}</LabelContent> : <>{LabelContent}</>
}

/** A column's label as text, for accessible names and announcements; the column's name stands in when it has no label, or a label component that can't be read as text */
export const useColumnLabelText = <L extends Line>(): (name: keyof Columns<L>) => string => {
    const columns = useColumns<L>()
    const translator = useTranslator()

    return useCallback(
        (name) => {
            const label = columns[name]?.label

            if (isTranslation(label)) {
                return translator.formatTranslation(label)
            }

            return typeof label === 'string' ? label : name
        },
        [columns, translator]
    )
}
